import _ from 'lodash';
import { List } from 'immutable';

import trainTime from '../lib/TrainTime';
import StationStore from '../stores/StationStore';
import { TicketTypes, TimeTypes, DurationTypes, FareClasses, API_ISO_FORMAT } from '../constants/AppConstants';
import PassengerRailcard from '../records/PassengerRailcard';
import Timeband from '../records/Timeband';
import CustomDuration from '../records/CustomDuration';
import TicketType from '../records/TicketType';
import PromoCode from '../records/PromoCode';

/**
 * Serialize an object based on a schema
 */
export function serialize(schema, source) {
    let serialized = {};
    _.reduce(schema, (reduction, serializer, name) => {
        const result = serializer(source[name], source, name);
        if (result !== null) {
            reduction[name] = result;
        }
        return reduction;
    }, serialized);
    return serialized;
}

/**
 * List of serializers, they take a value, source object and name of the field they are operating on
 */
export const serializers = {

    station(station) {
        return station.crs || station.nlc;
    },

    stationCrs(station) {
        return station.crs;
    },

    stationNlc(station) {
        return station.nlc;
    },

    code(obj) {
        return obj.code || null;
    },

    promoCode(promo) {
        return (promo && promo.promoCode) || null;
    },

    railcards(railcards) {
        const mapped = railcards
                    .filter(r => r.code !== '' && r.quantity > 0)
                    .map(r => r.code + 'x' + r.quantity);

        if (mapped.size > 0) {
            return mapped.toJS();
        }
        return null;
    },

    railcardCodes(railcards) {
        return railcards.filterNot((r) => r.code === null).map((r) => r.code).toJS();
    },

    date(date) {
        return date.format('YYYY-MM-DD');
    },

    time(dateTime) {
        return dateTime.format('HH:mm:ss');
    },

    dateTime(dateTime) {
        return dateTime.format('YYYY-MM-DDTHH:mm');
    },

    isoDateTime(dateTime) {
        return dateTime.format(API_ISO_FORMAT);
    },

    timeBand(timeBand, source, name) {
        const prefix = timeBand.arriveDepart === TimeTypes.ARRIVE ? TimeTypes.ARRIVE + '_at_' : '';
        if (name !== 'returnDateTime' || source.ticketType === TicketTypes.RETURN) {
            return prefix + timeBand.datetime.format('YYYY-MM-DDTHH:mm');
        }

        if (source.ticketType === TicketTypes.OPEN_RETURN) {
            return 'open';
        }

        return null;
    },

    identity: (value) => value,

    serializedIdentity: (value) => value.serialize(),

    durationStartDate(durationField) {
        return (x, source) => source[durationField].getStartDate().format('YYYY-MM-DD');
    },

    durations(durationField) {
        return (x, source) => {
            let result = [];
            if (source.flexi) { result.push('flexi'); } // Technically not a duration type
            if (source.weekly) { result.push(DurationTypes.WEEKLY); }
            if (source.fourWeekly) { result.push(DurationTypes.FOUR_WEEKLY); }
            if (source.monthly) { result.push(DurationTypes.MONTHLY); }
            if (source.quarterly) { result.push(DurationTypes.QUARTERLY); }
            if (source.sixMonthly) { result.push(DurationTypes.SIX_MONTHLY); }
            if (source.annual) { result.push(DurationTypes.ANNUAL); }
            if (source.custom) {
                result.push(source[durationField].getDuration().toJSON());
            }
            return result;
        };
    },

    boolean: (value) => value ? 'yes' : 'no',

    ticketType: (type) => type.code
};


/**
 * Deserialize params based on a schema
 */
export function deserialize(schema, params) {

    let results = _.map(schema, (deserializer, name) => deserializer(params[name], params, name));

    // Deserializers can return promises so we need to get all the results
    // and then reduce them into the deserialized object
    return Promise.all(results).then((values) => {
        return _.reduce(values,
            (result, value) => Object.assign({}, result, value),
            {});
    });
}


function splitDatetime(value) {
    var arriveDepart, datetime;
    if (value.match('_at_')) {
        [arriveDepart, datetime] = value.split('_at_');
    } else {
        arriveDepart = TimeTypes.DEPART;
        datetime = value;
    }

    arriveDepart = arriveDepart === TimeTypes.ARRIVE ? TimeTypes.ARRIVE : TimeTypes.DEPART;
    return [arriveDepart, datetime];
}

// Takes a date & returns a trainTime
// Actual date strings are passed straight onto trainTime()
// Accepts relative dates such as "today", "tomorrow", "+7days"
// And times such as "@10:45", "@17:30"
// You can combine the two: "today@10:00", "+7days@09:30"
export function searchDateToTrainTime (dateString) {
    if (dateString.match(/^\d/)) {
        return trainTime(dateString);
    }

    let parsed = trainTime();

    const [ date, time ] = dateString.split('@');
    if (date === 'tomorrow') {
        parsed = trainTime().add({ days: 1 });
    } else if (date) {
        const [ match, num, unit ] = date.match(/\+(\d+)\s*(.*)/) || [];
        if (match) {
            parsed = trainTime().add(num, unit);
        }
    }

    if (time) {
        const [ hours, minutes ] = time.split(':');
        parsed = parsed.set({ hours, minutes });
    }
    return parsed;
}

/**
 * List of deserializers
 *
 * A deserializer is passed a value, the source object and the name of the field it is operating on
 * Because deserializers can set multiple parameters they need to a return a map of the fields they
 * are setting on the result, or a promise that returns this
 */
export const deserializers = {

    boolean(value, source, name) {
        let result = Boolean(value);
        let str = String(value);
        if (str.toLowerCase() === 'false' || str.toLowerCase() === 'no' || parseInt(str, 0) === 0) {
            result = Boolean(false);
        }
        return { [name]: result };
    },

    identity: (value, source, name) => { return { [name]: value }; },

    station(value, source, name) {
        return StationStore.get(value).then((station) => {
            return { [name]: station };
        });
    },

    promoCode(value, source, name) {
        return { [name]: new PromoCode({ promoCode: value }) };
    },

    fareClass(value, source, name){
        if (value && _.includes(_.values(FareClasses), value)) {
            return { [name]: value };
        }

        return { [name]: FareClasses.STANDARD };
    },

    int(value, source, name) {
        value = parseInt(value, 10);
        if (!isNaN(value)) {
            return { [name]: value };
        }

        return { [name]: 0 };
    },

    railcards(railcards, source, name) {
        if (!railcards) { return {}; }

        const filtered = railcards.split(',')
              .map(x => x.split(/x(?=[^x]*$)/))
              .filter(x => x.length >= 2);

        if (filtered.length <= 0) { return {}; }

        const mapped = filtered.map(part => {
            let parsed = _.zipObject([ 'code', 'quantity' ], part);
            parsed.quantity = parseInt(parsed.quantity, 10);
            return new PassengerRailcard(parsed);
        });

        return { [name]: new List(mapped) };
    },

    timeBand(value, source, name) {
        if (!value) { return {}; }
        if (value === 'open') {
            return { ticketType: TicketTypes.OPEN_RETURN };
        }

        const [arriveDepart, datetime] = splitDatetime(value);

        let datetimeObj;

        if (name === 'returnDateTime' && datetime === 'return') {
            const outbound = splitDatetime(source.outboundDateTime)[1];
            datetimeObj = searchDateToTrainTime(outbound);
        } else {
            datetimeObj = searchDateToTrainTime(datetime);
        }

        if (!datetimeObj.isValid()) {
            datetimeObj = trainTime();
        }

        let result = { [name]: new Timeband({ arriveDepart, datetime: datetimeObj }) };
        if (name === 'returnDateTime') {
            result.ticketType = TicketTypes.RETURN;
        }

        return result;
    },

    date(value, source, name) {
        return { [name]: searchDateToTrainTime(value) };
    },

    durations(durations, source) {
        const startDate = searchDateToTrainTime(source.startDate);
        let custom = new CustomDuration({ startDate });
        let flags = {};

        const DurationTypesWithFlexi = Object.assign({}, DurationTypes, {FLEXI: 'flexi'});

        Object.keys(DurationTypesWithFlexi).forEach((flag) => (flags[DurationTypesWithFlexi[flag]] = false));

        if (durations) {
            durations.split(',').forEach((duration) => {
                if (_.find(DurationTypesWithFlexi, (type) => type === duration)) {
                    flags[duration] = true;
                } else {
                    flags.custom = true;
                    custom = custom.setDuration(trainTime.duration(duration));
                }
            });
        }
        custom = custom.setStartDate(startDate);

        return Object.assign(flags, { dates: custom });
    },

    ticketType(value, source, name) {
        if (!value) { return { [name]: new TicketType() }; }

        return { [name]: new TicketType({ code: value }) };
    }
};
